import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutGrid, CalendarDays, Users, LogOut, PlusCircle, Hammer, Contact, KeyRound, MoreHorizontal, Wallet } from "lucide-react";
import { NotificationToggle, NotificationPrompt, NotificationIconButton } from "./NotificationControls";

const navByRole = {
  admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutGrid },
    { to: "/jobs", label: "Jobs", icon: Hammer },
    { to: "/customers", label: "Customers", icon: Contact },
    { to: "/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/payments", label: "Payments", icon: Wallet },
    { to: "/users", label: "Users", icon: Users },
    { to: "/settings", label: "Integrations", icon: KeyRound },
  ],
  sales: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutGrid },
    { to: "/jobs", label: "Jobs", icon: Hammer },
    { to: "/customers", label: "Customers", icon: Contact },
    { to: "/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/payments", label: "Payments", icon: Wallet },
  ],
  team: [
    { to: "/dashboard", label: "My Jobs", icon: LayoutGrid },
    { to: "/calendar", label: "Calendar", icon: CalendarDays },
  ],
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const items = navByRole[user?.role] || [];
  const canCreate = user?.role === "admin" || user?.role === "sales";
  // Mobile bottom bar keeps up to 4 primary destinations; the rest go in a "More" sheet.
  const primary = items.slice(0, 4);
  const overflow = items.slice(4);
  const cols = primary.length + (overflow.length ? 1 : 0);

  return (
    <div className="min-h-screen flex bg-[#F9F9F8]">
      <aside className="hidden md:flex w-64 flex-col border-r border-[#E8E8E4] bg-white sticky top-0 h-screen">
        <div className="px-6 pb-6 pt-[calc(env(safe-area-inset-top)_+_1.5rem)] border-b border-[#E8E8E4]">
          <img src="/tilesmate-logo.png" alt="TilesMate" className="w-full h-auto" />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                  isActive ? "bg-[#1A1A1A] text-white" : "text-[#4A4A4A] hover:bg-[#F3F3F1]"
                }`
              }
            >
              <it.icon size={18} />
              {it.label}
            </NavLink>
          ))}
          {canCreate && (
            <button
              data-testid="new-job-btn"
              onClick={() => navigate("/jobs/new")}
              className="w-full mt-4 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold bg-[#2C2C2C] text-white hover:bg-[#1A1A1A] hover:-translate-y-[1px] transition-all"
            >
              <PlusCircle size={18} />
              New Job
            </button>
          )}
        </nav>
        <div className="px-3 pt-4 pb-[calc(env(safe-area-inset-bottom)_+_1rem)] border-t border-[#E8E8E4]">
          <NotificationToggle />
          <div className="flex items-center gap-3 px-3 py-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center text-sm font-semibold">
                {user?.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#1A1A1A] truncate">{user?.name}</div>
              <div className="text-xs text-[#737373] capitalize truncate">{user?.role}{user?.team_name ? ` · ${user.team_name}` : ""}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full mt-2 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-[#4A4A4A] hover:bg-[#F3F3F1] transition-all"
          >
            <LogOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden sticky top-0 z-20 backdrop-blur-xl bg-white/80 border-b border-[#E8E8E4] pb-3 pt-[calc(env(safe-area-inset-top)_+_0.75rem)] pl-[calc(env(safe-area-inset-left)_+_1rem)] pr-[calc(env(safe-area-inset-right)_+_1rem)] flex items-center justify-between">
          <img src="/tilesmate-logo.png" alt="TilesMate" className="h-7 w-auto" />
          <div className="flex items-center gap-1">
            <NotificationIconButton />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8 max-w-[1400px] w-full mx-auto pb-[calc(env(safe-area-inset-bottom)_+_5.75rem)] md:pb-8"><NotificationPrompt />{children}</main>
      </div>

      {/* Mobile bottom navigation (thumb-reachable) */}
      <nav data-testid="mobile-bottom-nav" className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-xl border-t border-[#E8E8E4] pb-[env(safe-area-inset-bottom)]">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
          {primary.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`mobile-nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `relative flex flex-col items-center justify-center gap-0.5 py-2 min-h-[58px] text-[11px] font-medium transition-colors active:bg-[#F3F3F1] ${isActive ? "text-[#1A1A1A]" : "text-[#737373]"}`}
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute top-0 h-0.5 w-10 rounded-full bg-[#E08A2B]" />}
                  <it.icon size={21} strokeWidth={isActive ? 2.4 : 2} />
                  <span className="truncate max-w-[68px]">{it.label}</span>
                </>
              )}
            </NavLink>
          ))}
          {overflow.length > 0 && (
            <button
              data-testid="mobile-nav-more"
              onClick={() => setMoreOpen(true)}
              className={`flex flex-col items-center justify-center gap-0.5 py-2 min-h-[58px] text-[11px] font-medium transition-colors active:bg-[#F3F3F1] ${moreOpen ? "text-[#1A1A1A]" : "text-[#737373]"}`}
            >
              <MoreHorizontal size={21} />
              <span>More</span>
            </button>
          )}
        </div>
      </nav>

      {/* Floating New Job button (admin/sales) */}
      {canCreate && pathname !== "/payments" && (
        <button
          data-testid="mobile-new-job-fab"
          onClick={() => navigate("/jobs/new")}
          aria-label="New job"
          className="md:hidden fixed z-30 right-4 bottom-[calc(env(safe-area-inset-bottom)_+_4.9rem)] w-14 h-14 rounded-full bg-[#2C2C2C] text-white shadow-[0_8px_24px_rgba(0,0,0,0.28)] flex items-center justify-center active:scale-95 transition-transform"
        >
          <PlusCircle size={26} />
        </button>
      )}

      {/* Mobile "More" sheet */}
      {moreOpen && (
        <div data-testid="mobile-more-sheet" className="md:hidden fixed inset-0 z-40" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-0 bg-black/40 animate-[fadeIn_.15s_ease]" />
          <div onClick={(e) => e.stopPropagation()} className="absolute bottom-0 inset-x-0 bg-white rounded-t-2xl p-4 pb-[calc(env(safe-area-inset-bottom)_+_1rem)] space-y-1 animate-[slideUp_.2s_ease]">
            <div className="w-10 h-1 bg-[#E8E8E4] rounded-full mx-auto mb-3" />
            <div className="flex items-center gap-3 px-3 py-2 mb-1">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center text-sm font-semibold">{user?.name?.[0]?.toUpperCase()}</div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#1A1A1A] truncate">{user?.name}</div>
                <div className="text-xs text-[#737373] capitalize truncate">{user?.role}{user?.team_name ? ` · ${user.team_name}` : ""}</div>
              </div>
            </div>
            {overflow.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setMoreOpen(false)}
                data-testid={`more-nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-[#1A1A1A] text-white" : "text-[#4A4A4A] active:bg-[#F3F3F1]"}`}
              >
                <it.icon size={19} />{it.label}
              </NavLink>
            ))}
            <div className="pt-2 mt-1 border-t border-[#E8E8E4]">
              <NotificationToggle />
              <button data-testid="mobile-logout-btn" onClick={logout} className="w-full flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium text-[#4A4A4A] active:bg-[#F3F3F1]"><LogOut size={19} /> Sign out</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
